import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { COOKIE_MODE, api } from '../api';
import { DEMO } from '../demo';
import { summariseDiff } from '../diff';
import { t } from '../i18n';
import { ActionMenu } from '../components/ActionMenu';
import { useShell } from '../components/AppLayout';
import { DryRunTab } from '../components/DryRunTab';
import { Interpolated } from '../components/Interpolated';
import { SubTabs, Tip } from '../components/PageHead';
import { ReviewSaveTab } from '../components/ReviewSaveTab';
import { RuleSetSettingsDialog } from '../components/RuleSetSettings';
import { RulesTab } from '../components/RulesTab';
import { Dialog } from '../components/Dialog';
import { VersionsTab } from '../components/VersionsTab';
import { When } from '../components/When';
import type { RuleKind } from '../hooks/useRuleSetDraft';
import { candidateWithOnly } from '../scopedPublish';
import { canEdit, canPublish } from '../session';
// 2c rule sets
import { SetRulesEditor } from '../components/SetRulesEditor';
import { isDemoRuleSetId } from '../demo/ruleSets';
import { useRuleSets } from '../hooks/useRuleSets';
import { setLabel } from '../ruleSets';
// R8b per-rule-set permissions
import { setAccessIn } from '../ruleSets';
import { mayOnSet } from '../session';
// Wave 5 rule set workspace
import { ruleSetForbiddenMessage } from '../api';
import { NAV_SHORT_LABEL } from '../navigation';
import { awaitsCopySeed, primaryIdIn, seededCopy, setViewIn } from '../ruleSets';
import { RULE_SETS_PATH, setRulesPath, type RuleFamily } from '../ruleSetRoutes';

/**
 * A rule set's page — Wave 5: the set is the workspace. `/rule-sets/<set>/transaction` and
 * `/rule-sets/<set>/pattern` are its two tabs, Transaction (RB) and Pattern base (PB), both
 * editing the SAME document: releasing from either tab releases the whole set, which is why the
 * version line is identical on both and says so (behind its `i`).
 *
 * The heading is the breadcrumb `Rule Sets › <set name>` with the set's id, primary and on/off
 * chips. The primary's document is the shell's (`state`); any other set gets the shell's second
 * draft (`otherSet`), so leaving the page does not lose it either. Everything below — rules, dry
 * run, settings, publish, history, set rules — works on that set, through
 * `/api/rulesets/{id}/rules…` (the primary's through `/api/rules…`, as ever).
 *
 * An archived set opens read-only (its history too). A duplicate still at its creation release
 * opens with its source's release as the draft (`awaitsCopySeed`), and says so.
 */

type TabId = 'rules' | 'dry-run';

/** What the version line can open over the screen — one at a time. */
type DialogId = 'settings' | 'release' | 'history' | 'set-rules';

/**
 * The "…" at the right of the version line (`ActionMenu`). Everything behind it is something
 * an operator opens now and then, not a place they work — the rule set's settings, the
 * release flow and the version history — so each opens as a dialog over the screen instead
 * of taking a tab. A rule row's "Publish" opens the same release dialog, scoped to that rule.
 *
 * Cookie mode: edits go into the draft, and Publish makes it live at once (the maker-checker
 * step is dropped for now). A Viewer gets no Publish anywhere — no menu item, no clickable
 * draft chip, no row Publish. Nor does a Viewer change the draft: no "+ Add rule", the rule
 * switches are disabled, a rule opens read-only, and so do the rule set's settings.
 */

export function RuleManagementScreen({ kind, setId }: { kind: RuleFamily; setId: string }) {
  const shell = useShell();
  const { session, showToast, otherSet } = shell;
  const [tab, setTab] = useState<TabId>('rules');
  const [dialog, setDialog] = useState<DialogId | null>(null);
  /** Set when the release dialog was opened from a rule row: release that rule only. */
  const [scope, setScope] = useState<{ kind: RuleKind; id: string } | null>(null);
  /** "+ Add rule" was pressed and the Rules tab has not opened the new-rule editor yet. */
  const [addRequest, setAddRequest] = useState<RuleKind | null>(null);
  /** A rule link (`?rule=<id>`, from Dashboard / Report / Log / Investigate) waiting for the Rules tab to open it. */
  const [openRequest, setOpenRequest] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // ---- which set: the route's --------------------------------------------------------
  const { list: registry } = useRuleSets(shell.settings);
  const primaryId = shell.state.loaded?.ruleSetId ?? primaryIdIn(registry);
  const manySets = COOKIE_MODE || DEMO;
  /**
   * Whether the route's set is the primary can be told: the shell's (primary) document is in,
   * or it failed, or the registry answered. Until then nothing is chosen — deciding early would
   * load the primary a second time as "another" set.
   */
  const resolved = primaryId !== undefined || !!shell.state.error || registry !== null;
  /** A set other than the primary is on screen (cookie mode or demo; password mode has one set). */
  const other = manySets && otherSet && resolved && setId !== primaryId ? setId : null;
  /** Sent back once: the toast re-renders the shell before the navigation lands. */
  const bounced = useRef(false);
  useEffect(() => {
    if (!other || !otherSet || otherSet.id === other || bounced.current) return;
    // One other set's draft at a time: one with changes is not dropped for this one.
    if (otherSet.id && otherSet.state.dirty) {
      bounced.current = true;
      navigate(setRulesPath(otherSet.id, kind), { replace: true });
      showToast(t('rulesets.picker.dirty.toast', { id: otherSet.id }), 'error');
      return;
    }
    otherSet.choose(other);
  }, [other, otherSet, kind, navigate, showToast]);
  const onOther = other !== null && otherSet?.id === other;
  const state = onOther ? otherSet!.state : shell.state;
  const settings = onOther ? otherSet!.settings : shell.settings;
  /** The draft on screen is the route's set's (never the previous set's while the next one loads). */
  const draftFits = resolved && (other === null || (onOther && state.draft?.ruleSetId === other));
  const demoSet = isDemoRuleSetId(other ?? undefined);
  /** The set's registry row — its name, chips and copy source; absent in password mode. */
  const view = setViewIn(registry, setId);
  const archived = !!view?.archived;

  const draft = draftFits ? state.draft : null;
  const only = kind === 'rb' ? 'ruleBased' : 'patternBased';

  // UX review P1-3: `?rule=<id>` opens that rule. A link guesses the family from the id; a
  // rule that lives on the other tab is handed over there, with the same link.
  const wanted = params.get('rule');
  useEffect(() => {
    if (!wanted || !draft) return;
    const here = (kind === 'rb' ? draft.ruleBased : draft.patternBased).some((r) => r.id === wanted);
    const there = (kind === 'rb' ? draft.patternBased : draft.ruleBased).some((r) => r.id === wanted);
    if (!here && there) {
      navigate(setRulesPath(setId, kind === 'rb' ? 'pb' : 'rb', wanted), { replace: true });
      return;
    }
    const next = new URLSearchParams(params);
    next.delete('rule');
    setParams(next, { replace: true });
    setTab('rules');
    setOpenRequest(wanted);
  }, [wanted, draft === null]);

  const releaseLabel = t(COOKIE_MODE ? 'menu.publish.label' : 'tab.review.label');
  // Password mode has no session and no role model: canPublish(null) is true.
  // 2c rule sets: a demo set is shown, never written. Wave 5: nor is an archived one.
  // R8b: the user's level on THIS set (from the registry listing) when the service sent one.
  const access = setAccessIn(registry, onOther ? other : primaryId);
  const mayPublish = (access !== undefined ? mayOnSet(session, access, 'publish') : canPublish(session)) && !demoSet && !archived;
  // The same for the draft itself: a Viewer reads every rule and changes none.
  const mayEdit = (access !== undefined ? mayOnSet(session, access, 'edit') : canEdit(session)) && !demoSet && !archived;

  // ---- Wave 5: a duplicate's first content is its source's release, as a draft ------------
  const copy = draftFits ? awaitsCopySeed(view, state.loaded) : null;
  const seeded = useRef(false);
  const [seedError, setSeedError] = useState<string | null>(null);
  useEffect(() => {
    // Once per visit: a draft discarded here stays discarded; one already edited is kept.
    if (!copy || seeded.current || !mayEdit || state.dirty || !state.loaded) return;
    seeded.current = true;
    const live = state.loaded;
    api.version({ ...settings, ruleSetId: copy.setId }, copy.version).then(
      (r) => state.setDraft(seededCopy(r.ruleSet, live)),
      // R8b: a source this account may not read says so in words.
      (e) => setSeedError(ruleSetForbiddenMessage(e) ?? (e instanceof Error ? e.message : String(e))),
    );
  }, [copy?.setId, copy?.version, mayEdit, state.loaded]);
  const copySource = copy ? setViewIn(registry, copy.setId) : undefined;

  const openDialog = (id: DialogId) => {
    setScope(null);
    setDialog(id);
  };
  const closeDialog = () => {
    setDialog(null);
    setScope(null);
  };
  const publishRule = (kind: RuleKind, id: string) => {
    setScope({ kind, id });
    setDialog('release');
  };
  const candidate =
    dialog === 'release' && scope && state.loaded && draft ? candidateWithOnly(state.loaded, draft, scope.kind, scope.id) : undefined;

  // Password mode knows one set: any other id in the address is that set.
  if (!manySets && primaryId && setId !== primaryId) return <Navigate to={setRulesPath(primaryId, kind)} replace />;

  const name = view?.name ?? setId;
  const isPrimary = setId === primaryId;
  const enabled = view ? view.enabled && !view.archived : isPrimary;

  return (
    <>
      {/* The heading is the breadcrumb: Rule Sets › <set name>, then what the set is. */}
      <div className="page-head">
        <h1 data-testid="set-heading">
          <span className="crumbs">
            <Link to={RULE_SETS_PATH} data-testid="set-crumb">
              {t('rulesets.title')}
            </Link>
            <span className="crumb-sep" aria-hidden="true">
              ›
            </span>
            <span data-testid="set-name">{name}</span>
          </span>
          {name !== setId && (
            <span className="mono small muted" data-testid="set-id">
              {setId}
            </span>
          )}
          {isPrimary && (
            <span className="chip accent" data-testid="set-primary-chip">
              {t('rulesets.primary.chip')}
            </span>
          )}
          {archived ? (
            <span className="chip mute" data-testid="set-archived-chip">
              {t('rulesets.archived.chip')}
            </span>
          ) : (
            <span className={enabled ? 'chip ok' : 'chip mute'} data-testid="set-enabled-chip">
              {t(enabled ? 'rulesets.enabled.on' : 'rulesets.enabled.off')}
            </span>
          )}
          {demoSet && <span className="chip warn">{t('master.demo.chip')}</span>}
          <Tip label={t('tip.screen.label')}>{t(`screen.${kind}.console.lead`)}</Tip>
        </h1>
      </div>

      {/* The set's two tabs — the two Rule Management screens, by their short names. */}
      <nav className="tabs basetabs" role="tablist" aria-label={t('rulesets.page.tabs.a11y')} data-testid="set-tabs">
        {(['rb', 'pb'] as const).map((k) => (
          <NavLink
            key={k}
            to={setRulesPath(setId, k)}
            role="tab"
            className="tab"
            aria-selected={k === kind}
            data-testid={`set-tab-${k}`}
          >
            {t(NAV_SHORT_LABEL[k] ?? `nav.${k}.label`)}
          </NavLink>
        ))}
      </nav>

      {archived && (
        <div className="banner warn" role="status" data-testid="set-archived" style={{ marginBottom: 12 }}>
          {t('rulesets.page.archived')}
        </div>
      )}
      {copy && (
        <div className="banner warn" role="status" data-testid="set-copy-banner" style={{ marginBottom: 12 }}>
          {t('rulesets.copy.banner', { set: copySource ? setLabel(copySource) : copy.setId, version: copy.version })}
          {seedError && <div className="small">{t('rulesets.copy.error', { detail: seedError })}</div>}
        </div>
      )}

      {/*
        Flat: the unreleased draft (only while there is one), "+ Add rule", and a "…" for the
        rest. RB and PB share one document and one version — that is behind the `i`, not a tree.
      */}
      <div className="verline">
        {/* UX review P2-1: what is live, always — the draft (if any) follows it. */}
        {state.loaded && draftFits && (
          <span className="small" data-testid="live-version">
            {t('set.live.label')} <span className="mono">{state.loaded.version}</span>
            {state.loaded.updatedUtc && (
              <span className="muted">
                {' · '}
                {t('set.live.since')} <When iso={state.loaded.updatedUtc} />
              </span>
            )}
          </span>
        )}
        {/* The draft's state, where the draft is edited — and the way to release it. */}
        {state.dirty &&
          draftFits &&
          (mayPublish ? (
            <button
              type="button"
              className="chip warn"
              data-testid="dirty-indicator"
              title={t('shell.dirty.tip')}
              aria-haspopup="dialog"
              onClick={() => openDialog('release')}
            >
              {`${t('set.draft.label', { version: draft?.version ?? '—' })} · ${summariseDiff(state.diff)}`}
            </button>
          ) : (
            <span className="chip warn" data-testid="dirty-indicator" title={t('shell.dirty.tip')}>
              {`${t('set.draft.label', { version: draft?.version ?? '—' })} · ${summariseDiff(state.diff)}`}
            </span>
          ))}
        {/* Demo mode has no engine behind it: one quiet line says the rules are not checked. */}
        <span className="spacer" />
        <Tip align="right">
          <Interpolated text={t('set.shared.note')} slot="{bold}">
            <b>{t('set.shared.bold')}</b>
          </Interpolated>
          {COOKIE_MODE && ` ${t('set.publish.note')}`}
        </Tip>
        <ActionMenu
          label={t('menu.more.a11y')}
          items={[
            { label: t('menu.settings.label'), onSelect: () => openDialog('settings') },
            { label: t('dlg.ver-history.title'), onSelect: () => openDialog('history') },
            // 2c rule sets (a): "≥ N rules of this set fire → action"
            ...(COOKIE_MODE || DEMO ? [{ label: t('rulesets.setRules.menu'), onSelect: () => openDialog('set-rules') }] : []),
            // The release last and set apart (UX review P1-5); it opens the release dialog, which confirms.
            ...(mayPublish ? [{ label: releaseLabel, onSelect: () => openDialog('release'), separated: true }] : []),
          ]}
        />
      </div>

      {/* 2c rule sets: the set could not be read (unknown, hidden, the service down). */}
      {onOther && state.error && (
        <div className="banner error" role="status" data-testid="set-error">
          {t('rulesets.picker.error', { id: other, detail: state.error })}
        </div>
      )}
      {/* The primary's load is the shell's to report; another set's is this page's. */}
      {other !== null && !draftFits && !(onOther && state.error) && <div className="empty">{t('shell.loading.document')}</div>}

      <Fragment>
        <SubTabs
          label={t(`screen.${kind}.title`)}
          active={tab}
          onSelect={(id) => setTab(id as TabId)}
          // Dry run is for whoever may change the draft — the BFF answers a Viewer 403. Demo mode
          // has no engine to run it, so the tab is not offered there (Wave 2-0).
          tabs={[
            { id: 'rules', label: t('tab.rules.label') },
            ...(mayEdit && !DEMO ? [{ id: 'dry-run', label: t('tab.dry-run.label') }] : []),
          ]}
        />
        {tab === 'rules' && draft && (
          <RulesTab
            state={state}
            only={only}
            settings={settings}
            onPublish={mayPublish ? publishRule : undefined}
            readOnly={!mayEdit}
            onToast={showToast}
            addRequest={addRequest}
            onAddHandled={() => setAddRequest(null)}
            // UX review P2-10: "+ Add rule" at the right of the rules table's head, like every list's primary action.
            onAdd={mayEdit ? () => setAddRequest(only) : undefined}
            openRequest={openRequest}
            onOpenHandled={(found) => {
              if (!found && openRequest) showToast(t('rules.link.missing', { id: openRequest }), 'error');
              setOpenRequest(null);
            }}
          />
        )}
        {tab === 'dry-run' && mayEdit && !DEMO && draft && <DryRunTab state={state} settings={settings} />}
      </Fragment>

      {dialog === 'settings' && <RuleSetSettingsDialog state={state} readOnly={!mayEdit} onClose={closeDialog} />}

      {/*
        One release dialog in both modes: `ReviewSaveTab`. Password mode saves through the
        decision API with a typed actor; cookie mode publishes through the BFF's
        `PUT /api/rules`, which names the actor from the session. Never open for a Viewer.
      */}
      {dialog === 'release' && mayPublish && (
        <Dialog title={scope ? t('row.publish.title', { id: scope.id }) : releaseLabel} wide plainBody onClose={closeDialog}>
          <ReviewSaveTab key={scope?.id ?? ''} state={state} settings={settings} onToast={showToast} candidate={candidate} scopeLabel={scope?.id} />
        </Dialog>
      )}

      {dialog === 'history' && (
        <Dialog title={t('dlg.ver-history.title')} wide plainBody onClose={closeDialog}>
          <VersionsTab state={state} settings={settings} session={session} onToast={showToast} mayPublish={access !== undefined || archived ? mayPublish : undefined} />
        </Dialog>
      )}

      {/* 2c rule sets (a) */}
      {dialog === 'set-rules' && draft && (
        <Dialog title={t('rulesets.setRules.title')} wide plainBody testid="set-rules-dialog" onClose={closeDialog}>
          <SetRulesEditor state={state} readOnly={!mayEdit} />
        </Dialog>
      )}
    </>
  );
}
